import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Home.css';

const Home = () => {
  const [questions, setQuestions] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [loading, setLoading] = useState(true);
  const [unitNotesStatus, setUnitNotesStatus] = useState({});
  const [downloadingUnit, setDownloadingUnit] = useState(null);

  useEffect(() => {
    fetchQuestions();
    checkAllUnitsStatus();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get('https://codecruxdsa.onrender.com/api/questions');
      setQuestions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setLoading(false);
    }
  };

  // Check status of all units
  const checkAllUnitsStatus = async () => {
    try {
      const response = await axios.get('https://codecruxdsa.onrender.com/api/notes/all');
      const statusMap = {};
      response.data.forEach(note => {
        statusMap[note.unit_number] = {
          exists: true,
          filename: note.filename,
          uploadedAt: note.uploaded_at
        };
      });
      setUnitNotesStatus(statusMap);
    } catch (error) {
      console.error('Error checking units status:', error);
    }
  };

  const filteredQuestions = questions.filter(q => {
    const difficultyMatch = selectedDifficulty === 'all' || q.difficulty === selectedDifficulty;
    const topicMatch = selectedTopic === 'all' || q.topic === selectedTopic;
    return difficultyMatch && topicMatch;
  });

  const topics = [...new Set(questions.map(q => q.topic))];

  // Handle unit notes download from Cloudinary
  const handleDownload = async (unit) => {
    setDownloadingUnit(unit);
    
    try {
      // Get the Cloudinary URL from backend
      const response = await axios.get(`https://codecruxdsa.onrender.com/api/notes/unit${unit}`);
      
      if (response.data.exists && response.data.url) {
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = response.data.url;
        link.setAttribute('download', response.data.filename || `Unit${unit}-DSA-Notes.pdf`);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(`Notes for Unit ${unit} are not available yet`);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      if (error.response && error.response.status === 404) {
        alert(`Notes for Unit ${unit} have not been uploaded yet`);
      } else {
        alert('Error downloading file. Please try again later.');
      }
    } finally {
      setDownloadingUnit(null);
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title fade-in">Master DSA with CodeCruxDSA</h1>
          <p className="hero-subtitle slide-up">Complete Data Structures & Algorithms for AKTU 2nd Year</p>
          <div className="hero-buttons slide-up delay-1">
            <a href="#questions" className="btn btn-primary">Explore Questions</a>
            <a href="#notes" className="btn btn-secondary">Download Notes</a>
          </div>
        </div>
        <div className="hero-animation">
          <div className="floating-card card-1">📚</div>
          <div className="floating-card card-2">💡</div>
          <div className="floating-card card-3">🚀</div>
        </div>
      </section>

      {/* Topics Section */}
      <section id="topics" className="section topics-section">
        <div className="container">
          <h2 className="section-title">Popular Topics</h2>
          <div className="topics-grid">
            {topics.map((topic, index) => (
              <div key={index} className="topic-card" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="topic-icon">📌</div>
                <h3>{topic}</h3>
                <p>{questions.filter(q => q.topic === topic).length} Questions</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Questions Section */}
      <section id="questions" className="section questions-section">
        <div className="container">
          <h2 className="section-title">Important Questions</h2>
          
          <div className="filters">
            <div className="filter-group">
              <label>Difficulty:</label>
              <select value={selectedDifficulty} onChange={(e) => setSelectedDifficulty(e.target.value)}>
                <option value="all">All</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Topic:</label>
              <select value={selectedTopic} onChange={(e) => setSelectedTopic(e.target.value)}>
                <option value="all">All Topics</option>
                {topics.map((topic, index) => (
                  <option key={index} value={topic}>{topic}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loader">Loading questions...</div>
          ) : (
            <div className="questions-list">
              {filteredQuestions.map((question, index) => (
                <div key={question.id} className="question-card" style={{animationDelay: `${index * 0.05}s`}}>
                  <div className="question-header">
                    <h3>{question.title}</h3>
                    <span className={`difficulty-badge ${question.difficulty}`}>
                      {question.difficulty}
                    </span>
                  </div>
                  
                  <div className="question-meta">
                    <span className="topic-tag">🏷️ {question.topic}</span>
                  </div>
                  
                  <div className="question-resources">
                    {question.youtube_link && (
                      <a href={question.youtube_link} target="_blank" rel="noopener noreferrer" className="resource-link youtube">
                        <i className="fab fa-youtube"></i> Watch Tutorial
                      </a>
                    )}
                    {question.pdf_link && (
                      <a href={question.pdf_link} target="_blank" rel="noopener noreferrer" className="resource-link pdf">
                        <i className="fas fa-file-pdf"></i> Download PDF
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Unit Notes Section - UPDATED WITH CLOUDINARY DOWNLOAD */}
      <section id="notes" className="section notes-section">
        <div className="container">
          <h2 className="section-title">Unit-wise Notes</h2>
          <p className="notes-subtitle">Download comprehensive DSA notes for each unit</p>
          <div className="units-grid">
            {[1, 2, 3, 4, 5].map((unit, index) => (
              <div key={unit} className="unit-card" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="unit-number">Unit {unit}</div>
                <h3>DSA Notes</h3>
                <p>Complete notes for Unit {unit}</p>
                
                {/* Show upload status if available */}
                {unitNotesStatus[unit] && (
                  <div className="upload-info">
                    <small>
                      📄 {unitNotesStatus[unit].filename}
                    </small>
                    <small className="upload-date">
                      Uploaded: {new Date(unitNotesStatus[unit].uploadedAt).toLocaleDateString()}
                    </small>
                  </div>
                )}
                
                <button 
                  className={`download-btn ${!unitNotesStatus[unit] ? 'disabled' : ''}`}
                  onClick={() => handleDownload(unit)}
                  disabled={downloadingUnit === unit || !unitNotesStatus[unit]}
                >
                  {downloadingUnit === unit ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Downloading...
                    </>
                  ) : !unitNotesStatus[unit] ? (
                    <>
                      <i className="fas fa-clock"></i> Coming Soon
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download"></i> Download PDF
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;